import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Project, Service, TeamMember, Client, Message, GeneralSettings, SiteContent } from '../types';
import { useUI } from './UIContext';
import { useAuth } from './AuthContext';
import { contentTR, contentEN, contentIT } from '../content';

const API_URL = import.meta.env.PROD ? '/api/index.php' : (import.meta.env.VITE_API_URL || '/api/index.php');

// Initial Data Constants (Moved from SiteContext)
const initialProjects: Project[] = [
    { id: 1, title: 'Piattaforma Finanziaria', category: 'Web', client: 'Milano Finanza S.p.A.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800', description: 'Dashboard finanziaria basata su React e Node.js per aziende corporate, offrendo analisi dei dati in tempo reale.', tech: ['React', 'Node.js', 'Socket.io'], status: 'Sviluppo', progress: 65, budget: '€120.000', deadline: '2024-11-20', isActive: true, gallery: ['https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200', 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200'], challenge: 'Elaborazione istantanea di grandi set di dati e visualizzazione millimetrica nell\'interfaccia.', solution: 'Flusso di dati ottimizzato utilizzando l\'architettura WebSocket e il sistema di caching Redis.', features: ['Flusso Dati Real-Time', 'Modulo Reportistica', 'Autorizzazione Ruoli', 'Dark/Light Mode'], liveLink: '#', repoLink: '#' },
    { id: 2, title: 'Moda Milano E-Commerce', category: 'Mobile', client: 'Vogue Italia', image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80&w=800', description: 'Applicazione mobile supportata da AR (Realtà Aumentata) sviluppata per il settore della moda di lusso.', tech: ['Flutter', 'Firebase'], status: 'Design', progress: 30, budget: '€450.000', deadline: '2024-12-15', isActive: true, gallery: ['https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80&w=1200'], challenge: 'Necessità di un motore AR ad alte prestazioni per la prova virtuale degli abiti.', solution: 'Librerie ARKit e ARCore ottimizzate e integrate in Flutter.', features: ['Camerino Virtuale', 'Ricerca Vocale', 'Suggerimenti AI'], liveLink: '#', repoLink: '#' },
    { id: 3, title: 'EcoEnergia IoT', category: 'Sistemi & IoT', client: 'GreenEnergy Italia', image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=800', description: 'Piattaforma di monitoraggio remoto per centrali di energia rinnovabile in Toscana.', tech: ['Python', 'MQTT'], status: 'Completato', progress: 100, budget: '€85.000', deadline: '2024-10-30', isActive: true, gallery: ['https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1200'], challenge: 'Trasferimento ininterrotto dei dati dai sensori in aree remote.', solution: 'Integrità dei dati garantita con architettura offline-first e protocollo MQTT.', features: ['Controllo Remoto', 'Previsione Guasti AI', 'Analisi Storica'], liveLink: '#', repoLink: '#' },
];

const initialServices: Service[] = [
    { id: 1, title: 'Sviluppo Web', desc: 'Applicazioni web moderne, performanti e compatibili con la SEO.', iconName: 'Code', tags: ['React', 'Next.js'], isActive: true },
    { id: 2, title: 'Sviluppo Mobile', desc: 'Soluzioni native e ibride perfette per iOS e Android.', iconName: 'Smartphone', tags: ['Flutter', 'Swift'], isActive: true },
    { id: 3, title: 'Design UI/UX', desc: 'Design di interfacce estetiche e funzionali orientate all\'utente.', iconName: 'PenTool', tags: ['Figma'], isActive: true },
    { id: 4, title: 'Soluzioni Cloud', desc: 'Architetture server sicure, flessibili ed economiche.', iconName: 'Cloud', tags: ['AWS', 'Docker'], isActive: true },
];

const initialTeam: TeamMember[] = [
    { id: 1, name: 'Giulia Rossi', role: 'Senior Frontend Dev', email: 'giulia@greysoftware.it', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150', status: 'online', isActive: true },
    { id: 2, name: 'Marco Bianchi', role: 'UI/UX Designer', email: 'marco@greysoftware.it', image: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?auto=format&fit=crop&q=80&w=150', status: 'offline', isActive: true },
    { id: 3, name: 'Alessandro Verdi', role: 'Backend Developer', email: 'alessandro@greysoftware.it', image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=150', status: 'online', isActive: true },
];

const initialClients: Client[] = [
    { id: 1, company: 'Milano Finanza S.p.A.', contact: 'Dott. Ferrari', email: 'info@milanofinanza.it', totalSpent: '€850.000', status: 'Active', lastActive: '2 ore fa', isActive: true },
    { id: 2, company: 'Vogue Italia', contact: 'Sofia Ricci', email: 'sofia@vogue.it', totalSpent: '€320.000', status: 'Active', lastActive: '1 giorno fa', isActive: true },
];

const initialMessages: Message[] = [
    { id: 1, name: 'Luca Romano', email: 'luca@test.it', projectType: 'web', message: 'Vorremmo ricevere un preventivo per il nostro sito e-commerce.', date: '22.12.2024', isRead: false },
];

const initialSettings: GeneralSettings = {
    contactEmail: 'info@greysoftware.it',
    contactPhone: '+39 02 4264 5678',
    address: 'Via Alessandro Volta 12, 20121 Milano MI, Italia',
    socials: { twitter: 'https://twitter.com', linkedin: 'https://linkedin.com', instagram: 'https://instagram.com', github: 'https://github.com' }
};

interface DataContextType {
    projects: Project[];
    services: Service[];
    team: TeamMember[];
    clients: Client[];
    messages: Message[];
    generalSettings: GeneralSettings;
    siteContent: SiteContent;
    isLoading: boolean;

    // CRUD Actions
    addProject: (p: Omit<Project, 'id'>) => void;
    updateProject: (id: number, p: Partial<Project>) => void;
    deleteProject: (id: number) => void;

    addService: (s: Omit<Service, 'id'>) => void;
    updateService: (id: number, s: Partial<Service>) => void;
    deleteService: (id: number) => void;

    addTeamMember: (t: Omit<TeamMember, 'id'>) => void;
    updateTeamMember: (id: number, t: Partial<TeamMember>) => void;
    deleteTeamMember: (id: number) => void;

    addClient: (c: Omit<Client, 'id'>) => void;
    updateClient: (id: number, c: Partial<Client>) => void;
    deleteClient: (id: number) => void;

    addMessage: (m: Omit<Message, 'id' | 'date' | 'isRead'>) => void;
    markMessageRead: (id: number) => void;
    deleteMessage: (id: number) => void;

    updateSettings: (s: Partial<GeneralSettings>) => void;
    updateSiteContent: (c: Partial<SiteContent>) => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { language, addToast } = useUI();
    const { token } = useAuth();

    const [isLoading, setIsLoading] = useState(true);

    const [projects, setProjects] = useState<Project[]>(initialProjects);
    const [services, setServices] = useState<Service[]>(initialServices);
    const [team, setTeam] = useState<TeamMember[]>(initialTeam);
    const [clients, setClients] = useState<Client[]>(initialClients);
    const [messages, setMessages] = useState<Message[]>(initialMessages);
    const [generalSettings, setGeneralSettings] = useState<GeneralSettings>(initialSettings);

    // Content based on language
    const [siteContent, setSiteContent] = useState<SiteContent>(contentIT);
    // Store all languages content to enable saving
    const [allContent, setAllContent] = useState<any>({ tr: contentTR, en: contentEN, it: contentIT });

    useEffect(() => {
        // When language changes, update the displayed siteContent from our master allContent
        if (allContent && allContent[language]) {
            setSiteContent(allContent[language]);
        }
    }, [language, allContent]);

    // Fetch Data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch(API_URL);
                const data = await res.json();
                if (data) {
                    if (data.projects) setProjects(data.projects);
                    if (data.services) setServices(data.services);
                    if (data.team) setTeam(data.team);
                    if (data.clients) setClients(data.clients);
                    if (data.messages) setMessages(data.messages);
                    if (data.settings) setGeneralSettings(data.settings);
                    
                    if (data.content) {
                        setAllContent(data.content);
                        // Initial set for current language
                        if (data.content[language]) {
                            setSiteContent(data.content[language]);
                        }
                    }
                }
            } catch (e) {
                console.error("Fetch Error", e);
                // addToast('Veri yüklenemedi, varsayılanlar kullanılıyor.', 'error'); // Optional
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []); // Run once on mount

    // Sync Helper
    const syncToApi = async (section: string, data: any) => {
        try {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'X-Auth-Token': token || ''
            };

            const res = await fetch(API_URL, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ section, data })
            });

            if (!res.ok) {
                throw new Error(`HTTP Error: ${res.status}`);
            }
        } catch (e) {
            console.error("Sync Failed", e);
            addToast('Sunucuya kaydedilemedi! Lütfen sayfayı yenileyip tekrar deneyin.', 'error');
        }
    };

    // CRUD Implementations
    const addProject = (p: Omit<Project, 'id'>) => {
        const newData = [...projects, { ...p, id: Date.now() }];
        setProjects(newData);
        syncToApi('projects', newData);
        addToast('Proje eklendi', 'success');
    };
    const updateProject = (id: number, p: Partial<Project>) => {
        const newData = projects.map(item => item.id === id ? { ...item, ...p } : item);
        setProjects(newData);
        syncToApi('projects', newData);
        addToast('Proje güncellendi', 'success');
    };
    const deleteProject = (id: number) => {
        const newData = projects.filter(item => item.id !== id);
        setProjects(newData);
        syncToApi('projects', newData);
        addToast('Proje silindi', 'info');
    };

    const addService = (s: Omit<Service, 'id'>) => {
        const newData = [...services, { ...s, id: Date.now() }];
        setServices(newData);
        syncToApi('services', newData);
        addToast('Hizmet eklendi', 'success');
    };
    const updateService = (id: number, s: Partial<Service>) => {
        const newData = services.map(item => item.id === id ? { ...item, ...s } : item);
        setServices(newData);
        syncToApi('services', newData);
        addToast('Hizmet güncellendi', 'success');
    };
    const deleteService = (id: number) => {
        const newData = services.filter(item => item.id !== id);
        setServices(newData);
        syncToApi('services', newData);
        addToast('Hizmet silindi', 'info');
    };

    const addTeamMember = (t: Omit<TeamMember, 'id'>) => {
        const newData = [...team, { ...t, id: Date.now() }];
        setTeam(newData);
        syncToApi('team', newData);
        addToast('Üye eklendi', 'success');
    };
    const updateTeamMember = (id: number, t: Partial<TeamMember>) => {
        const newData = team.map(item => item.id === id ? { ...item, ...t } : item);
        setTeam(newData);
        syncToApi('team', newData);
        addToast('Üye güncellendi', 'success');
    };
    const deleteTeamMember = (id: number) => {
        const newData = team.filter(item => item.id !== id);
        setTeam(newData);
        syncToApi('team', newData);
        addToast('Üye silindi', 'info');
    };

    const addClient = (c: Omit<Client, 'id'>) => {
        const newData = [...clients, { ...c, id: Date.now() }];
        setClients(newData);
        syncToApi('clients', newData);
        addToast('Müşteri eklendi', 'success');
    };
    const updateClient = (id: number, c: Partial<Client>) => {
        const newData = clients.map(item => item.id === id ? { ...item, ...c } : item);
        setClients(newData);
        syncToApi('clients', newData);
        addToast('Müşteri güncellendi', 'success');
    };
    const deleteClient = (id: number) => {
        const newData = clients.filter(item => item.id !== id);
        setClients(newData);
        syncToApi('clients', newData);
        addToast('Müşteri silindi', 'info');
    };

    const addMessage = (m: Omit<Message, 'id' | 'date' | 'isRead'>) => {
        const newData = [...messages, { ...m, id: Date.now(), date: new Date().toLocaleDateString(), isRead: false }];
        setMessages(newData);
        syncToApi('messages', newData);
        addToast('Mesajınız iletildi', 'success');
    };
    const markMessageRead = (id: number) => {
        const newData = messages.map(m => m.id === id ? { ...m, isRead: true } : m);
        setMessages(newData);
        syncToApi('messages', newData);
    };
    const deleteMessage = (id: number) => {
        const newData = messages.filter(m => m.id !== id);
        setMessages(newData);
        syncToApi('messages', newData);
        addToast('Mesaj silindi', 'info');
    };

    const updateSettings = (s: Partial<GeneralSettings>) => {
        const newData = { ...generalSettings, ...s };
        setGeneralSettings(newData);
        syncToApi('settings', newData);
        addToast('Ayarlar güncellendi', 'success');
    };
    const updateSiteContent = (c: Partial<SiteContent>) => {
        // 1. Update local view immediately
        const newData = { ...siteContent, ...c };
        setSiteContent(newData);

        // 2. Update master content record
        const newAllContent = { ...allContent, [language]: newData };
        setAllContent(newAllContent);

        // 3. Sync full content to API
        syncToApi('content', newAllContent);
        
        addToast('İçerik güncellendi', 'success');
    };

    return (
        <DataContext.Provider value={{
            projects, services, team, clients, messages, generalSettings, siteContent, isLoading,
            addProject, updateProject, deleteProject,
            addService, updateService, deleteService,
            addTeamMember, updateTeamMember, deleteTeamMember,
            addClient, updateClient, deleteClient,
            addMessage, markMessageRead, deleteMessage,
            updateSettings, updateSiteContent
        }}>
            {children}
        </DataContext.Provider>
    );
};
